import {
  useCompany,
  useParentCompany,
  useDealersByCompany,
  useContractTypesByCompany,
  useUsersByCompany,
  useGetImage,
} from '@api'
import {FormFieldType, ImageType, NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef, GridColumnVisibilityModel} from '@mui/x-data-grid'
import {pageLinksAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {
  activeCell,
  countries,
  FormFieldItem,
  getCountryFlag,
  isCompanyUser,
  roles,
  renderClickableCell,
  currencies,
  useViewNavigate,
} from 'common'
import {useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

import {ComposedComponents} from '@components'

export enum CompanyTab {
  GENERAL,
  DEALERS,
  CONTRACT_TYPES,
  USERS,
}

export function useCompanyViewModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [activeTab, setActiveTab] = useState<CompanyTab>(CompanyTab.GENERAL)
  const navigate = useViewNavigate()
  const {t} = useTranslation()
  const {id} = useParams()

  const {data: dealersData} = useDealersByCompany(id)
  const {data: companyData} = useCompany(id)
  const {data: usersData} = useUsersByCompany(id)
  const {data: parentCompanyData} = useParentCompany(companyData?.parentCompanyId)
  const {data: contractTypesData} = useContractTypesByCompany(companyData?.id)
  const {data: image} = useGetImage(ImageType.COMPANY, id)

  const dealerRows = useMemo(() => {
    if (!dealersData) return []

    return dealersData.map(item => ({
      name: item.name,
      address: item.address,
      city: item.city,
      companyNo: item.companyNo,
      dealerNo: item.dealerNo,
      company: companyData?.name,
      active: item.active,
      id: item.id,
    }))
  }, [dealersData])

  const userRows = useMemo(() => {
    if (!usersData) return []
    else
      return usersData.map(item => ({
        name: item.fullName,
        email: item.email,
        active: item.active,
        company: companyData?.name,
        dealer: dealersData?.find(dealer => dealer.id === item.dealerId)?.name || '-',
        role: roles(t).find(roleItem => roleItem.code === item.role)?.name || '',
        id: item.id,
      }))
  }, [usersData, dealersData])

  const contractTypeRows = useMemo(() => {
    if (!contractTypesData) return []
    else
      return contractTypesData.map(item => ({
        name: item.name,
        type: item.type,
        description: item.note,
        company: companyData?.name,
        active: item.active,
        id: item.id,
      }))
  }, [contractTypesData])

  /**
   * Edit button should be available only for Administrator Users
   */
  const isEditButtonVisible = !isCompanyUser()

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Resources), link: NavigationPathKey.RESOURCES_DASHBOARD},
        {title: t(TranslationKey['Company search']), link: NavigationPathKey.COMPANY_SEARCH},
        {title: t(TranslationKey['Company view'])},
      ]),
    [],
  )

  /**
   * User table columns configuration
   */
  const userColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 0.8,
      hideable: false,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.USER_VIEW}/${params.row.id}`)),
    },
    {field: 'email', headerName: t(TranslationKey.Email), flex: 1},
    {field: 'dealer', headerName: t(TranslationKey.Dealer), flex: 1},
    {field: 'role', headerName: t(TranslationKey.Role), flex: 0.8},
    activeCell(t),
  ]

  /**
   * Dealer table columns configuration
   */
  const dealerColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 1,
      hideable: false,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.DEALER_VIEW}/${params.row.id}`)),
    },
    {field: 'dealerNo', headerName: t(TranslationKey['Dealer number']), flex: 1},
    {field: 'address', headerName: t(TranslationKey.Address), flex: 1.2},
    {field: 'city', headerName: t(TranslationKey.City), flex: 1},
    activeCell(t),
  ]

  /**
   * Dealer table visibility model
   */
  const dealerTableVisiblityModel: GridColumnVisibilityModel = {
    address: false,
  }

  /**
   * Contract Type table column configuration
   */
  const contractTypeColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 0.8,
      hideable: false,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.CONTRACT_TYPE_VIEW}/${params.row.id}`)),
    },
    {field: 'type', headerName: t(TranslationKey.Type), width: 100},
    {field: 'description', headerName: t(TranslationKey.Description), flex: 1},
    activeCell(t),
  ]

  /**
   * Tab buttons configuration
   */
  const tabButtons: ComposedComponents.TabButtonProps<CompanyTab>[] = [
    {
      label: t(TranslationKey['General information']),
      key: CompanyTab.GENERAL,
      onClick: () => setActiveTab(CompanyTab.GENERAL),
    },
    {
      label: t(TranslationKey.Dealers),
      key: CompanyTab.DEALERS,
      onClick: () => setActiveTab(CompanyTab.DEALERS),
    },
    {
      label: t(TranslationKey['Contract types']),
      key: CompanyTab.CONTRACT_TYPES,
      onClick: () => setActiveTab(CompanyTab.CONTRACT_TYPES),
    },
    {
      label: t(TranslationKey.Users),
      key: CompanyTab.USERS,
      onClick: () => setActiveTab(CompanyTab.USERS),
    },
  ]

  /**
   * Company form fields configuration - general section
   */
  const companyGeneralFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Company name']),
      text: companyData?.name,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Amplio partner']),
      text: parentCompanyData?.name || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Description),
      text: companyData?.description || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Country),
      text: countries(t).find(item => item.isoCode === companyData?.country)?.name,
      icon: companyData?.country && getCountryFlag(companyData?.country),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Currency),
      text: companyData?.country ? currencies[companyData?.country] : '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Active),
      text: companyData?.active ? t(TranslationKey.Active) : t(TranslationKey.Inactive),
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Notes),
      text: companyData?.note || '-',
    },
  ]

  /**

  /**
   * A function handling the Back button click
   */
  const onPressBack = () => {
    navigate(NavigationPathKey.COMPANY_SEARCH)
  }

  /**
   * A function handling Edit button click
   */
  const onPressEdit = (): void => {
    navigate(`${NavigationPathKey.COMPANY_CREATION}/${id}`)
  }

  /**
   * Create button should only be visible for Administrator users
   */
  const showCreateButton = !isCompanyUser()

  return {
    companyGeneralFormFields,
    image,
    t,
    showCreateButton,
    dealerTableVisiblityModel,
    contractTypeColumns,
    contractTypeRows,
    tabButtons,
    activeTab,
    dealerColumns,
    dealerRows,
    isEditButtonVisible,
    userColumns,
    userRows,
    onPressBack,
    onPressEdit,
    navigate,
  }
}
